import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MutableRefObject,
  type ReactNode,
} from 'react';
import {
  Button,
  Container,
  FileInput,
  Group,
  Radio,
  SegmentedControl,
  Skeleton,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea,
  UnstyledButton,
} from '@mantine/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { CaptionTrack, MediaUsage, Problem, VideoResolveResponse } from '@nelnela/contracts';
import { api, ApiError } from '../lib/api';
import { fmtTime, formatBytes, mediaUsageLine } from '../lib/text';
import { runUpload, UploadAbortedError, type UploadProgress } from '../lib/upload';
import { useStudy } from '../app/study';
import { createYouTubePlayer, type YouTubePlayerHandle } from '../lib/player/youtube';
import {
  CaptionFetchError,
  fetchTrack,
  listTracks,
  parseCaptionText,
  previewCount,
  type Cue,
} from '../lib/captions';
import { IconBack } from '../components/watch/icons';

const UNENTITLED_LINE = 'Adding videos is part of the paid layer.';
const NOT_YOUTUBE_LINE = "That doesn't look like a YouTube link.";
const NO_SUBS_LINE = 'This video has no subtitles yet — paste or upload a file to add it.';
const FETCH_FAILED_LINE = "Couldn't fetch subtitles from the source — paste them here instead.";
const EMBED_BLOCKED_LINE = "This video can't be played embedded here — ";
const ADD_FAILED_LINE = "Couldn't add the video — try again.";
const PASTE_CHOICE = 'paste';

/**
 * F3 Add a video — one screen, progressive (frames 12d → 3b → 3c): paste a link → resolve →
 * confirm (the embedded player itself, one status line, discovered tracks or paste/upload,
 * two toggles) → fetch/parse → create → open Watch (the job pill lives there). The Android
 * share target lands here as `/add?url=`. Adding emits no events (content, not knowledge).
 *
 * M4c (ADR-0006 §5) adds a second way in — an uploaded file — for the ONE learner whose lane is
 * enabled. `me.media !== null` below is the whole privacy model: it is the only condition in this
 * screen that mentions the lane, and when it is false the wizard renders exactly what it rendered
 * before the lane existed. No disabled segment, no hint, no copy about uploads anywhere.
 */
export function AddVideo() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const { entitled, me } = useStudy();
  const media = me.media;
  const [mode, setMode] = useState<'link' | 'upload'>('link');
  // Switching segments unmounts the upload branch, which aborts whatever is in flight — so the
  // switch is held while bytes are moving rather than throwing the upload away without a word.
  const [uploading, setUploading] = useState(false);
  const [url, setUrl] = useState(() => params.get('url') ?? '');
  const [resolving, setResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [resolved, setResolved] = useState<VideoResolveResponse | null>(null);

  const resolve = useCallback(async () => {
    const u = url.trim();
    if (!u || !entitled) return;
    setResolving(true);
    setResolveError(null);
    try {
      const r = await api.videoResolve({ url: u });
      setResolved(r);
    } catch (e) {
      const status = e instanceof ApiError ? e.problem.status : 0;
      if (status === 422) setResolveError(NOT_YOUTUBE_LINE);
      else if (status === 403) setResolveError(UNENTITLED_LINE);
      else if (status === 429 && e instanceof ApiError) setResolveError(e.problem.title);
      else setResolveError("Couldn't check that link — try again.");
    } finally {
      setResolving(false);
    }
  }, [url, entitled]);

  const back = () => {
    if (resolved) setResolved(null);
    else nav('/watch');
  };

  return (
    <Container size={640} px={0} pb="xl">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          height: 44,
          padding: '0 4px',
          gap: 2,
        }}
      >
        <UnstyledButton
          onClick={back}
          aria-label={resolved ? 'Back to the link' : 'Back to the library'}
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--nn-muted)',
          }}
        >
          <IconBack />
        </UnstyledButton>
        <Text fz={13.5} fw={600}>
          Add a video
        </Text>
      </div>

      {!resolved ? (
        <Stack gap="sm" px="md" pt={6}>
          {media !== null && (
            <SegmentedControl
              value={mode}
              onChange={(v) => setMode(v === 'upload' ? 'upload' : 'link')}
              data={[
                { value: 'link', label: 'Paste a link' },
                { value: 'upload', label: 'Upload a file' },
              ]}
              size="xs"
              disabled={uploading}
              style={{ alignSelf: 'flex-start' }}
            />
          )}
          {media !== null && mode === 'upload' ? (
            <UploadStep usage={media} entitled={entitled} onBusy={setUploading} onCancel={back} />
          ) : (
            <>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  void resolve();
                }}
              >
                <Stack gap="sm">
                  <TextInput
                    value={url}
                    onChange={(e) => {
                      setUrl(e.currentTarget.value);
                      setResolveError(null);
                    }}
                    placeholder="paste a YouTube link…"
                    inputMode="url"
                    autoComplete="off"
                    autoFocus
                    size="md"
                    aria-label="YouTube link"
                    error={resolveError ?? undefined}
                  />
                  <Button
                    type="submit"
                    disabled={!entitled || !url.trim() || resolving}
                    loading={resolving}
                    fullWidth
                    size="md"
                  >
                    Continue
                  </Button>
                </Stack>
              </form>
              <Text fz="sm" c="dimmed">
                {entitled
                  ? "Only videos with subtitles can join — you'll see what's available before anything is added."
                  : UNENTITLED_LINE}
              </Text>
            </>
          )}
        </Stack>
      ) : (
        <ConfirmStep
          key={resolved.externalId}
          resolved={resolved}
          entitled={entitled}
          onCancel={back}
        />
      )}
    </Container>
  );
}

// ------------------------------------------------ subtitles: paste / upload (both branches)

type ParsedSubs = { cues: Cue[]; lines: number } | { error: string } | null;

/**
 * The paste/upload subtitle source. Both branches of step 1 need exactly this — a link's
 * discovered track can always be declined, and an upload has no source captions at all — so it
 * lives in one hook + one field pair rather than being forked per branch.
 */
interface SubtitleSource {
  pasted: string;
  setPasted: (text: string) => void;
  file: File | null;
  onFile: (f: File | null) => void;
  fileError: string | null;
  parsed: ParsedSubs;
  /** the parsed cues, or null when nothing usable has been pasted yet */
  cues: Cue[] | null;
  /** the "N lines" preview count, or null */
  lines: number | null;
  ref: MutableRefObject<HTMLTextAreaElement | null>;
  focus: () => void;
}

function useSubtitleSource(opts: { onEdit?: () => void; onFileRead?: () => void } = {}) {
  const { onEdit, onFileRead } = opts;
  const [pasted, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const ref = useRef<HTMLTextAreaElement | null>(null);
  const parsed = useMemo<ParsedSubs>(() => {
    if (!pasted.trim()) return null;
    try {
      const cues = parseCaptionText(pasted);
      return { cues, lines: previewCount(cues) };
    } catch {
      return { error: "That doesn't look like SRT or VTT — nothing parsed." };
    }
  }, [pasted]);
  const src: SubtitleSource = {
    pasted,
    setPasted: (t) => {
      setText(t);
      onEdit?.();
    },
    file,
    onFile: (f) => {
      setFile(f);
      setFileError(null);
      if (!f) return;
      f.text().then(
        (text) => {
          setText(text);
          onFileRead?.();
        },
        () => setFileError("Couldn't read that file."),
      );
    },
    fileError,
    parsed,
    cues: parsed && 'cues' in parsed ? parsed.cues : null,
    lines: parsed && 'lines' in parsed ? parsed.lines : null,
    ref,
    focus: () => ref.current?.focus(),
  };
  return src;
}

function SubtitleFields({ src, disabled }: { src: SubtitleSource; disabled?: boolean }) {
  return (
    <Stack gap={8} mt={8}>
      <Textarea
        ref={src.ref}
        value={src.pasted}
        onChange={(e) => src.setPasted(e.currentTarget.value)}
        placeholder="drop .srt / .vtt or paste text"
        autosize
        minRows={3}
        maxRows={8}
        spellCheck={false}
        autoCorrect="off"
        autoCapitalize="off"
        disabled={disabled}
        error={
          src.parsed && 'error' in src.parsed ? src.parsed.error : (src.fileError ?? undefined)
        }
        aria-label="Subtitle text"
      />
      <FileInput
        value={src.file}
        onChange={src.onFile}
        accept=".srt,.vtt,.xml,text/plain,text/vtt,application/x-subrip"
        placeholder="or choose a file…"
        clearable
        size="sm"
        disabled={disabled}
        aria-label="Subtitle file"
      />
    </Stack>
  );
}

function SubtitlesLabel() {
  return (
    <Text
      fz={10.5}
      mt={16}
      style={{ letterSpacing: '.07em', color: 'var(--nn-faint)', textTransform: 'uppercase' }}
    >
      Subtitles
    </Text>
  );
}

// ------------------------------------------------ step 2 + 3

type Busy = 'idle' | 'fetching' | 'creating';

function ConfirmStep({
  resolved,
  entitled,
  onCancel,
}: {
  resolved: VideoResolveResponse;
  entitled: boolean;
  onCancel: () => void;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const video = useMemo(
    () => ({ provider: resolved.provider, externalId: resolved.externalId }),
    [resolved.provider, resolved.externalId],
  );

  // --- the embedded player: proves embeddability (101/150) and gives listTracks a player.
  const [boxEl, setBoxEl] = useState<HTMLDivElement | null>(null);
  const [playerError, setPlayerError] = useState<number | null>(null);
  const [tracks, setTracks] = useState<CaptionTrack[] | null>(null); // null = discovering
  const [choice, setChoice] = useState<string | null>(null); // 'track:<lang>:<kind>' | 'paste'
  const pastedRef = useRef(''); // read by the late-discovery callback below
  useEffect(() => {
    if (resolved.provider !== 'youtube') {
      setTracks([]);
      return;
    }
    if (!boxEl) return; // the box mounts on the first commit; discovery starts then
    let cancelled = false;
    const inner = document.createElement('div');
    boxEl.appendChild(inner);
    const handle: YouTubePlayerHandle = createYouTubePlayer(inner, resolved.externalId, {
      onError: (code) => {
        if (!cancelled) setPlayerError(code);
      },
    });
    handle.ready
      .then(() => (cancelled ? [] : listTracks(video, handle.raw())))
      .catch(() => [] as CaptionTrack[])
      .then((t) => {
        if (!cancelled) setTracks(t);
      });
    // The captions module often only knows its tracklist once the video has started loading
    // (an unstarted, cued player answers []). If the learner presses play in the embed, look
    // once more; late finds are merged in (and preselected unless something was pasted).
    let rediscovered = false;
    const offState = handle.adapter.onStateChange((s) => {
      if (rediscovered || cancelled || (s !== 'playing' && s !== 'buffering')) return;
      rediscovered = true;
      void listTracks(video, handle.raw()).then((t) => {
        if (cancelled || t.length === 0) return;
        setTracks((prev) => (prev && prev.length > 0 ? prev : t));
        const first = t[0];
        if (!pastedRef.current.trim() && (first.lang === 'ka' || first.lang.startsWith('ka-')))
          setChoice(`track:${first.lang}:${first.kind}`);
      });
    });
    // A player that never becomes ready (API blocked, embed error before ready) must not leave
    // the wizard "discovering" forever.
    const giveUp = setTimeout(() => {
      if (!cancelled) setTracks((prev) => prev ?? []);
    }, 12_000);
    return () => {
      cancelled = true;
      clearTimeout(giveUp);
      offState();
      handle.destroy();
      if (inner.parentNode === boxEl) boxEl.removeChild(inner);
    };
  }, [boxEl, resolved.provider, resolved.externalId, video]);
  const embedBlocked = playerError === 101 || playerError === 150;

  // --- subtitle source: one discovered track, or paste/upload (always available)
  const trackKey = (t: CaptionTrack) => `track:${t.lang}:${t.kind}`;
  useEffect(() => {
    if (choice !== null || tracks === null) return;
    // Georgian preselected (tracks come sorted Georgian-first); otherwise paste/upload.
    const first = tracks[0];
    const georgian = !!first && (first.lang === 'ka' || first.lang.startsWith('ka-'));
    setChoice(georgian ? `track:${first.lang}:${first.kind}` : PASTE_CHOICE);
  }, [tracks, choice]);
  const chosenTrack = tracks?.find((t) => trackKey(t) === choice) ?? null;

  const [addError, setAddError] = useState<{ text: string; existingVideoId?: string } | null>(null);
  const subs = useSubtitleSource({
    onEdit: () => setAddError(null),
    onFileRead: () => setChoice(PASTE_CHOICE),
  });
  pastedRef.current = subs.pasted;
  const parsed = subs.parsed;
  const pastedCues = subs.cues;

  // --- toggles
  const [cleanup, setCleanup] = useState(true);
  const [translate, setTranslate] = useState(true);

  // --- add
  const [busy, setBusy] = useState<Busy>('idle');
  const [fetchFailed, setFetchFailed] = useState(false);
  const [lineCount, setLineCount] = useState<number | null>(null);

  const usingTrack = choice !== null && choice !== PASTE_CHOICE && chosenTrack !== null;
  const canAdd =
    entitled &&
    !embedBlocked &&
    busy === 'idle' &&
    (usingTrack || (choice === PASTE_CHOICE && pastedCues !== null));

  const add = async () => {
    if (!canAdd) return;
    setAddError(null);
    let cues: Cue[];
    let fromTrack = false;
    if (usingTrack && chosenTrack) {
      setBusy('fetching');
      try {
        cues = await fetchTrack(video, chosenTrack.lang, chosenTrack.kind);
        fromTrack = true;
      } catch {
        // Expected today (unsigned timedtext → empty): drop into the paste branch, don't abort.
        setBusy('idle');
        setFetchFailed(true);
        setChoice(PASTE_CHOICE);
        setTimeout(() => subs.focus(), 0);
        return;
      }
    } else if (pastedCues) {
      cues = pastedCues;
    } else return;
    setLineCount(previewCount(cues));
    setBusy('creating');
    try {
      const res = await api.videoCreate({
        targetLang: 'ka',
        baseLang: 'en',
        provider: resolved.provider,
        externalId: resolved.externalId,
        title: resolved.title ?? undefined,
        durationMs: resolved.durationMs ?? undefined,
        channelExternalId: resolved.channelExternalId ?? undefined,
        channelName: resolved.channelName ?? undefined,
        cues,
        captionSource: fromTrack ? 'source_captions' : 'user_upload',
        requestCleanup: cleanup,
        requestTranslate: cleanup ? true : translate,
      });
      void qc.invalidateQueries({ queryKey: ['videos'] });
      // Hand the started job to Watch (its pill seeds from it) — including a job that failed to
      // start (`status:'error'`), which videoGet's runningJob would never surface.
      nav(`/watch/${res.video.id}`, { replace: true, state: res.job ? { job: res.job } : null });
    } catch (e) {
      setBusy('idle');
      setAddError(createErrorLine(e));
    }
  };

  const channelLine = [
    resolved.channelName ?? resolved.channel?.name ?? null,
    resolved.durationMs != null ? fmtTime(resolved.durationMs) : null,
    `youtube.com/watch?v=${resolved.externalId}`,
  ]
    .filter(Boolean)
    .join(' · ');

  const status = resolved.existingVideoId ? (
    <StatusLine>
      <b>Already in the library</b> —{' '}
      <Link to={`/watch/${resolved.existingVideoId}`} style={{ color: 'var(--nn-link)' }}>
        open it
      </Link>
    </StatusLine>
  ) : resolved.willBe === 'shared' ? (
    <StatusLine accent>
      <b>From an official channel</b> — this will be added to the shared library for everyone.
    </StatusLine>
  ) : (
    <StatusLine>
      <b>This will be private to you</b> — only videos from verified official channels join the
      shared library.
    </StatusLine>
  );

  return (
    <Stack gap={0} px="md" pt={6}>
      <div
        ref={setBoxEl}
        className="nn-player"
        style={{
          position: 'relative',
          aspectRatio: '16 / 9',
          borderRadius: 9,
          overflow: 'hidden',
          background: `#000 url(https://i.ytimg.com/vi/${encodeURIComponent(resolved.externalId)}/hqdefault.jpg) center / cover no-repeat`,
        }}
      />
      {embedBlocked && (
        <Text fz="sm" mt={8}>
          {EMBED_BLOCKED_LINE}
          <a
            href={`https://www.youtube.com/watch?v=${encodeURIComponent(resolved.externalId)}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: 'var(--nn-link)' }}
          >
            Watch on YouTube ↗
          </a>
        </Text>
      )}
      {playerError != null && !embedBlocked && (
        <Text fz="xs" c="dimmed" mt={8}>
          The player reported an error ({playerError}).
        </Text>
      )}
      <Text fz={15} mt={11} lang="ka" style={{ color: 'var(--nn-text-strong)' }}>
        {resolved.title ?? resolved.externalId}
      </Text>
      <Text fz={11.5} c="dimmed" mt={2}>
        {channelLine}
      </Text>
      <div style={{ marginTop: 12 }}>{status}</div>

      {!resolved.existingVideoId && (
        <>
          <SubtitlesLabel />
          <Radio.Group
            value={choice ?? ''}
            onChange={(v) => {
              setChoice(v);
              setAddError(null);
            }}
            mt={8}
          >
            <Stack gap={7}>
              {tracks === null && (
                <Group gap={10} px={12} py={10}>
                  <Skeleton height={15} width={15} circle />
                  <Skeleton height={13} width={160} />
                </Group>
              )}
              {tracks?.map((t) => {
                const k = trackKey(t);
                const selected = choice === k;
                return (
                  <Radio.Card
                    key={k}
                    value={k}
                    radius={9}
                    style={{
                      borderColor: selected
                        ? 'color-mix(in srgb, var(--nn-accent) 55%, transparent)'
                        : 'var(--nn-hairline)',
                    }}
                  >
                    <Group gap={10} px={12} py={10} wrap="nowrap">
                      <Radio.Indicator size="sm" />
                      <div>
                        <Text fz={13} lang={t.lang}>
                          {t.name ?? t.lang} — {t.kind}
                        </Text>
                        <Text fz={10.5} c="dimmed">
                          {t.kind === 'auto' ? 'auto — will need cleanup' : 'manual'}
                        </Text>
                      </div>
                    </Group>
                  </Radio.Card>
                );
              })}
              <Radio.Card
                value={PASTE_CHOICE}
                radius={9}
                style={{
                  borderColor:
                    choice === PASTE_CHOICE
                      ? 'color-mix(in srgb, var(--nn-accent) 55%, transparent)'
                      : 'var(--nn-hairline)',
                }}
              >
                <Group gap={10} px={12} py={10} wrap="nowrap">
                  <Radio.Indicator size="sm" />
                  <div>
                    <Text fz={13}>Paste / upload subtitles (.srt / .vtt)</Text>
                    {parsed && 'lines' in parsed && (
                      <Text fz={10.5} c="dimmed">
                        {parsed.lines} lines
                      </Text>
                    )}
                  </div>
                </Group>
              </Radio.Card>
            </Stack>
          </Radio.Group>
          {tracks !== null && tracks.length === 0 && !pastedCues && !fetchFailed && (
            <Text fz="sm" c="dimmed" mt={8}>
              {NO_SUBS_LINE}
            </Text>
          )}
          {fetchFailed && (
            <Text fz="sm" mt={8}>
              {FETCH_FAILED_LINE}
            </Text>
          )}
          {choice === PASTE_CHOICE && <SubtitleFields src={subs} />}

          <Stack gap={10} mt={14}>
            <Switch
              checked={cleanup}
              onChange={(e) => setCleanup(e.currentTarget.checked)}
              label="Clean up the transcript now"
              description="Sentences and speakers via the AI helper — it translates too."
              size="sm"
            />
            <Switch
              checked={cleanup ? true : translate}
              disabled={cleanup}
              onChange={(e) => setTranslate(e.currentTarget.checked)}
              label="Translate to English"
              description={cleanup ? 'Included in the cleanup.' : undefined}
              size="sm"
            />
          </Stack>

          <Stack gap={9} mt={18}>
            <Button
              size="md"
              fullWidth
              disabled={!canAdd}
              loading={busy !== 'idle'}
              onClick={() => void add()}
            >
              Add and open
            </Button>
            {busy === 'fetching' && (
              <Text fz="sm" c="dimmed" ta="center">
                Fetching subtitles…
              </Text>
            )}
            {busy === 'creating' && (
              <Text fz="sm" c="dimmed" ta="center">
                Adding{lineCount != null ? ` · ${lineCount} lines` : ''}…
              </Text>
            )}
            {!entitled && (
              <Text fz="sm" c="dimmed" ta="center">
                {UNENTITLED_LINE}
              </Text>
            )}
            {addError && (
              <Text fz="sm" ta="center">
                {addError.text}
                {addError.existingVideoId && (
                  <>
                    {' — '}
                    <Link
                      to={`/watch/${addError.existingVideoId}`}
                      style={{ color: 'var(--nn-link)' }}
                    >
                      open it
                    </Link>
                  </>
                )}
              </Text>
            )}
            <UnstyledButton
              onClick={onCancel}
              style={{
                minHeight: 40,
                fontSize: 12.5,
                color: 'var(--nn-muted)',
                textAlign: 'center',
              }}
            >
              Cancel
            </UnstyledButton>
          </Stack>
        </>
      )}
    </Stack>
  );
}

// ------------------------------------------------ upload branch (M4c, one learner)

const FASTSTART_LINE = 'MP4 with faststart plays best';
const TOO_LARGE_LINE = 'This file is too large.';
const INTERRUPTED_LINE = 'Upload interrupted — try again.';

type UploadPhase = 'idle' | 'uploading' | 'creating';

/** What the local <video> told us about the file — sent with the create so the server records
 *  duration/dimensions without ever decoding the file itself. */
interface Probe {
  durationMs?: number;
  width?: number;
  height?: number;
}

/** "S01E03 - part 1.mp4" → "S01E03 - part 1". A dotless name is its own title. */
function titleFromFilename(name: string): string {
  const dot = name.lastIndexOf('.');
  const stem = (dot > 0 ? name.slice(0, dot) : name).trim();
  return stem || name;
}

/** One calm line per upload failure class. 404 is deliberately indistinguishable from any other
 *  server refusal: it means the lane vanished under us, and the wizard must not say the word. */
function uploadErrorLine(e: unknown): string {
  if (e instanceof ApiError) {
    switch (e.problem.status) {
      case 413:
        return TOO_LARGE_LINE;
      case 415:
      case 422:
        // The server's own sentence — it names the formats to convert to. The Zod boundary puts
        // it in `errors[0].message`, NOT in `detail`, so a bare `detail ?? title` prints
        // "Validation failed" and the learner is told nothing: the one thing a refused .mov has
        // to say is how to fix it. (`accept` on a file input is a hint — drag-and-drop, "All
        // Files", and a provider that hands over `type: ''` all reach this.)
        return e.problem.detail ?? e.problem.errors?.[0]?.message ?? e.problem.title;
      case 404:
        return ADD_FAILED_LINE;
      default:
        break;
    }
  }
  return INTERRUPTED_LINE;
}

/**
 * Step 1's "Upload a file" branch. Rendered only from `me.media !== null`, so this component
 * never mounts for anyone but Jeff (ADR-0006 §5) — which is also why nothing in it is written to
 * degrade gracefully for a learner without the lane: there is no such learner here.
 *
 * The file itself never transits one request: `runUpload` slices it into parts and the Worker
 * relays them into an R2 multipart upload. Everything after that — cues, toggles, create,
 * navigate — is the same path a YouTube link takes.
 */
function UploadStep({
  usage: seed,
  entitled,
  onBusy,
  onCancel,
}: {
  usage: MediaUsage;
  entitled: boolean;
  /** told while bytes are in flight, so the segment switch (which would unmount us) can wait */
  onBusy: (busy: boolean) => void;
  onCancel: () => void;
}) {
  const nav = useNavigate();
  const qc = useQueryClient();

  // `me` is read once at boot and memoized by StudyProvider, so its usage numbers go stale after
  // the first upload of a session; ask the route itself, seeded with what we already have.
  const usage = useQuery({
    queryKey: ['media', 'usage'],
    queryFn: () => api.mediaUsage(),
    initialData: seed,
    staleTime: 60_000,
  }).data;

  const [file, setFile] = useState<File | null>(null);
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [probe, setProbe] = useState<Probe | null>(null);
  const [phase, setPhase] = useState<UploadPhase>('idle');
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const busy = phase !== 'idle';

  const subs = useSubtitleSource({
    onEdit: () => setAddError(null),
    onFileRead: () => setAddError(null),
  });
  const [cleanup, setCleanup] = useState(true);
  const [translate, setTranslate] = useState(true);

  // The preview's src. A blob URL pins the whole file (gigabytes) until it is revoked, so every
  // pick revokes the previous one and unmounting revokes the last.
  useEffect(() => {
    if (!file) {
      setObjectUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);

  useEffect(() => {
    onBusy(phase === 'uploading');
    return () => onBusy(false);
  }, [phase, onBusy]);

  // Leaving mid-upload loses the parts already sent (the server's cron reaps the orphan), so ask
  // once. Registered only while uploading: a listener left behind would prompt on every later
  // navigation.
  useEffect(() => {
    if (phase !== 'uploading') return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [phase]);

  // In-app navigation away unmounts us; stop pushing parts for a screen nobody is on. Mount-scoped
  // so it fires only on unmount — the successful path clears the ref before it navigates.
  useEffect(() => () => abortRef.current?.abort(), []);

  const pickVideo = (f: File | null) => {
    setFile(f);
    setProbe(null);
    setAddError(null);
  };

  // `file.size > 0`: an empty file plans no parts, and `runUpload` documents that the wizard is
  // the one that refuses it.
  const canAdd = entitled && !busy && file !== null && file.size > 0 && subs.cues !== null;

  const add = async () => {
    if (!canAdd || !file || !subs.cues) return;
    setAddError(null);
    setProgress(null);
    const controller = new AbortController();
    abortRef.current = controller;
    setPhase('uploading');
    let mediaId: string;
    let mediaDurationMs: number | null = null;
    try {
      const { media } = await runUpload(file, {
        onProgress: setProgress,
        signal: controller.signal,
        meta: { durationMs: probe?.durationMs, width: probe?.width, height: probe?.height },
      });
      mediaId = media.id;
      mediaDurationMs = media.durationMs;
    } catch (e) {
      abortRef.current = null;
      setPhase('idle');
      // A cancelled upload is not a failure: back to the idle form, file still chosen, no line.
      if (!(e instanceof UploadAbortedError)) setAddError(uploadErrorLine(e));
      return;
    }
    abortRef.current = null;
    setPhase('creating');
    try {
      const res = await api.videoCreate({
        targetLang: 'ka',
        baseLang: 'en',
        provider: 'upload',
        externalId: mediaId,
        title: titleFromFilename(file.name),
        durationMs: mediaDurationMs ?? probe?.durationMs,
        cues: subs.cues,
        captionSource: 'user_upload',
        requestCleanup: cleanup,
        requestTranslate: cleanup ? true : translate,
      });
      void qc.invalidateQueries({ queryKey: ['videos'] });
      void qc.invalidateQueries({ queryKey: ['media', 'usage'] });
      // Same hand-off as the link branch: Watch's job pill seeds from the started job.
      nav(`/watch/${res.video.id}`, { replace: true, state: res.job ? { job: res.job } : null });
    } catch (e) {
      setPhase('idle');
      setAddError(createErrorLine(e).text);
    }
  };

  const metaLine = [
    probe?.durationMs != null ? fmtTime(probe.durationMs) : null,
    probe?.width && probe?.height ? `${probe.width}×${probe.height}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  // Report tone, no bar: there is no quota, so the number itself is the whole point. The wording
  // is shared with Me's card (lib/text.ts) so the two screens can never drift apart about what
  // Jeff has stored.
  const usageLine =
    mediaUsageLine(usage) + (file ? ` · this file is ${formatBytes(file.size)}` : '');

  return (
    <Stack gap={0}>
      <FileInput
        value={file}
        onChange={pickVideo}
        accept="video/mp4,video/webm"
        placeholder="choose a video file…"
        clearable
        size="md"
        disabled={busy}
        aria-label="Video file"
      />
      <Text fz="sm" c="dimmed" mt={8}>
        {FASTSTART_LINE}
      </Text>

      {objectUrl && (
        <div
          style={{
            position: 'relative',
            aspectRatio: '16 / 9',
            borderRadius: 9,
            overflow: 'hidden',
            background: '#000',
            marginTop: 12,
          }}
        >
          {/* Also the metadata probe: duration/videoWidth/videoHeight for the create call. */}
          <video
            key={objectUrl}
            src={objectUrl}
            controls
            playsInline
            preload="metadata"
            onLoadedMetadata={(e) => {
              const v = e.currentTarget;
              setProbe({
                durationMs: Number.isFinite(v.duration) ? Math.round(v.duration * 1000) : undefined,
                width: v.videoWidth || undefined,
                height: v.videoHeight || undefined,
              });
            }}
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}
          />
        </div>
      )}
      {file && (
        <Text fz={15} mt={11} style={{ color: 'var(--nn-text-strong)' }}>
          {titleFromFilename(file.name)}
        </Text>
      )}
      {metaLine && (
        <Text fz={11.5} c="dimmed" mt={2}>
          {metaLine}
        </Text>
      )}
      <Text fz={11.5} c="dimmed" mt={file ? 2 : 12}>
        {usageLine}
      </Text>

      {/* An upload has no source captions, so there is no track list — only paste/upload. */}
      <SubtitlesLabel />
      <SubtitleFields src={subs} disabled={busy} />

      <Stack gap={10} mt={14}>
        <Switch
          checked={cleanup}
          onChange={(e) => setCleanup(e.currentTarget.checked)}
          label="Clean up the transcript now"
          description="Sentences and speakers via the AI helper — it translates too."
          size="sm"
          disabled={busy}
        />
        <Switch
          checked={cleanup ? true : translate}
          disabled={cleanup || busy}
          onChange={(e) => setTranslate(e.currentTarget.checked)}
          label="Translate to English"
          description={cleanup ? 'Included in the cleanup.' : undefined}
          size="sm"
        />
      </Stack>

      <Stack gap={9} mt={18}>
        <Button size="md" fullWidth disabled={!canAdd} loading={busy} onClick={() => void add()}>
          Add and open
        </Button>
        {phase === 'uploading' ? (
          <UploadProgressLine
            progress={progress}
            totalBytes={file?.size ?? 0}
            onCancel={() => abortRef.current?.abort()}
          />
        ) : phase === 'creating' ? (
          <Text fz="sm" c="dimmed" ta="center">
            Adding{subs.lines != null ? ` · ${subs.lines} lines` : ''}…
          </Text>
        ) : (
          subs.lines != null && (
            <Text fz="sm" c="dimmed" ta="center">
              {subs.lines} lines
            </Text>
          )
        )}
        {!entitled && (
          <Text fz="sm" c="dimmed" ta="center">
            {UNENTITLED_LINE}
          </Text>
        )}
        {addError && (
          <Text fz="sm" ta="center">
            {addError}
          </Text>
        )}
        {!busy && (
          <UnstyledButton
            onClick={onCancel}
            style={{
              minHeight: 40,
              fontSize: 12.5,
              color: 'var(--nn-muted)',
              textAlign: 'center',
            }}
          >
            Cancel
          </UnstyledButton>
        )}
      </Stack>
    </Stack>
  );
}

/** The upload's status line — it takes the slot the "N lines" preview holds when idle. */
function UploadProgressLine({
  progress,
  totalBytes,
  onCancel,
}: {
  progress: UploadProgress | null;
  totalBytes: number;
  onCancel: () => void;
}) {
  const total = progress?.totalBytes ?? totalBytes;
  const done = progress?.doneBytes ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((done / total) * 100)) : 0;
  const etaMs = progress?.etaMs ?? null;
  const eta = etaMs != null && Number.isFinite(etaMs) && etaMs > 0 ? fmtTime(etaMs) : null;
  return (
    <Stack gap={6}>
      <div
        role="progressbar"
        aria-label="Upload progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={pct}
        style={{
          height: 3,
          borderRadius: 999,
          background: 'var(--nn-hairline)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            height: '100%',
            width: `${pct}%`,
            background: 'var(--nn-accent)',
            transition: 'width 250ms linear',
          }}
        />
      </div>
      <Group justify="space-between" gap={8} wrap="nowrap">
        <Text fz="xs" c="dimmed" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {pct}% · {formatBytes(done)} of {formatBytes(total)}
          {eta ? ` · ${eta} left` : ''}
        </Text>
        <UnstyledButton
          onClick={onCancel}
          style={{ minHeight: 32, fontSize: 12.5, color: 'var(--nn-muted)', paddingInline: 4 }}
        >
          Cancel
        </UnstyledButton>
      </Group>
    </Stack>
  );
}

function StatusLine({ children, accent }: { children: ReactNode; accent?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 9,
        alignItems: 'flex-start',
        background: 'var(--nn-panel)',
        border: '1px solid var(--nn-hairline)',
        borderRadius: 9,
        padding: '10px 12px',
      }}
    >
      {accent && (
        <svg
          width="15"
          height="15"
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--nn-accent)"
          strokeWidth="1.9"
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ flexShrink: 0, marginTop: 2 }}
          aria-hidden
        >
          <polyline points="5,12.5 10,17.5 19,7.5" />
        </svg>
      )}
      <Text fz={12.5} lh={1.55}>
        {children}
      </Text>
    </div>
  );
}

/** One calm line per failure class for videoCreate (F3 errors); 409 may carry the existing id. */
function createErrorLine(e: unknown): { text: string; existingVideoId?: string } {
  if (e instanceof CaptionFetchError) return { text: FETCH_FAILED_LINE };
  if (!(e instanceof ApiError)) return { text: ADD_FAILED_LINE };
  const p = e.problem as Problem & {
    existingVideoId?: string;
    extra?: { existingVideoId?: string };
  };
  const existingVideoId = p.existingVideoId ?? p.extra?.existingVideoId;
  switch (p.status) {
    case 409:
      return existingVideoId
        ? { text: 'Already in the library', existingVideoId }
        : { text: p.title };
    case 422:
      return { text: p.detail ?? p.title };
    case 403:
      return { text: UNENTITLED_LINE };
    case 429:
      return { text: p.title };
    default:
      return { text: ADD_FAILED_LINE };
  }
}
